import { useNavigate } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { P } from "../../../../AbstractElements";
import { useEffect, useState } from "react";
import useIsMobile from "../../../../hooks/useInMobile";
import { getFormattedDateTime } from "../../../../utils/dateUtils";
import MobileOnlyModal from "../punchAttendance/MobileOnlyModal";
import ShiftInfo from "./ShiftInfo";
import AttendanceActionButton from "./AttendanceActionButton";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { useGetEmployeeDetailsQuery } from "../../../../ReduxToolkit/api/employee";
import { skipToken } from "@reduxjs/toolkit/query";

const MarkAttendanceStatus = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [restrictedModal, setRestrictModal] = useState<boolean>(false);
  const { day, formattedDay, month, weekday } = getFormattedDateTime();

  const { loggedInEmployee } = useEmployeeProfile();

  const employeeLoginId = loggedInEmployee?.id;

  const { data: employeeProfileDetails } = useGetEmployeeDetailsQuery(
    employeeLoginId ? String(employeeLoginId) : skipToken,
    { refetchOnMountOrArgChange: true }
  );

  const dateInfo = `${day}${formattedDay} ${month} (${weekday})`;

  const employeeAttendanceData =
    employeeProfileDetails?.data.attendance_data ?? null;

  // derive base times
  const inTime = employeeAttendanceData?.in_time; // e.g. "10:09"
  const outTime = employeeAttendanceData?.out_time;
  const workingHoursFromAPI = employeeAttendanceData?.working_hours ?? "00:00:00";

  // display live timer
  const [clockInTime, setClockInTime] = useState<string>(workingHoursFromAPI);

  const formatTime = (hours: number, minutes: number, seconds: number) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  };

  // compute difference between now and in_time
  const calculateElapsedTime = (inTime: string) => {
    try {
      const today = new Date();
      const [inHours, inMinutes] = inTime.split(":").map(Number);
      const inDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        inHours,
        inMinutes,
        0
      );

      const diffMs = today.getTime() - inDate.getTime();
      const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      return formatTime(hours, minutes, seconds);
    } catch {
      return "00:00:00";
    }
  };

  useEffect(() => {
    if (inTime && !outTime) {
      // set initial duration
      setClockInTime(calculateElapsedTime(inTime));

      // live ticking
      const timer = setInterval(() => {
        setClockInTime(calculateElapsedTime(inTime));
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [inTime, outTime]);

  useEffect(() => {
    if (!isMobile) setRestrictModal(true);
  }, [isMobile]);

  const handleButtonClick = () => {
    navigate("/employee-zone/mark-attendance");
  };

  return (
    <Row className="pt-3 px-2">
      {isMobile && (
        <Col md="12">
          <Card>
            <CardBody>
              <div className="project-box py-3 px-2">
                <P className="text-danger f-w-400">
                  *Use mobile data to mark your attendance instead of Wi-Fi, as
                  Wi-Fi may cause location discrepancies.
                </P>
                <ShiftInfo dateInfo={dateInfo} />
                <AttendanceActionButton
                  data={employeeAttendanceData}
                  clockInTime={clockInTime}
                  onClick={handleButtonClick}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      )}

      {!isMobile && (
        <MobileOnlyModal
          isOpen={restrictedModal}
          handleOnClick={() => navigate("/dashboard")}
        />
      )}
    </Row>
  );
};

export default MarkAttendanceStatus;
